import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  FaShoppingCart, FaUser, FaBars, FaTimes,
  FaChevronDown, FaChevronRight, FaSignOutAlt, FaCog,
  FaSwimmingPool, FaHome, FaShoppingBag, FaList,
  FaStar, FaInfoCircle, FaEnvelope, FaInstagram, FaFacebook,
  FaTiktok, FaPercent, FaFeatherAlt,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useSiteContent } from '../hooks/useSiteContent';
import logo from '../assets/logo.jpeg';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen]   = useState(false);
  const [isUserOpen, setIsUserOpen]   = useState(false);
  const [scrolled, setScrolled]       = useState(false);
  const { currentUser, logout, isAdmin } = useAuth();
  const { cartCount } = useCart();
  const { content } = useSiteContent();
  const navigate = useNavigate();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* close drawer when clicking outside */
  useEffect(() => {
    if (!isMenuOpen && !isUserOpen) return;
    const fn = (e) => {
      if (!e.target.closest('.main-nav') && !e.target.closest('.menu-toggle'))
        setIsMenuOpen(false);
      if (!e.target.closest('.user-menu-wrap') && !e.target.closest('.user-menu-btn'))
        setIsUserOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [isMenuOpen, isUserOpen]);

  const navLinks = [
    { path: '/',          label: 'Home',        icon: <FaHome /> },
    { path: '/shop',      label: 'Shop',        icon: <FaShoppingBag /> },
    { path: '/categories',label: 'Categories',  icon: <FaList /> },
    { path: '/blog',      label: 'Journal',     icon: <FaFeatherAlt /> },
    { path: '/about',     label: 'About',       icon: <FaInfoCircle /> },
    { path: '/contact',   label: 'Contact',     icon: <FaEnvelope /> },
  ];

  const handleLogout = () => {
    logout();
    setIsUserOpen(false);
    navigate('/');
  };

  const close = () => { setIsMenuOpen(false); setIsUserOpen(false); };

  return (
    <>
      {/* ── Announcement strip ── */}
      {content.shipping?.announcement && (
        <div className="topbar">
          <span>{content.shipping.announcement}</span>
        </div>
      )}

      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-inner">

          {/* ── LEFT: hamburger (mobile only) ── */}
          <button
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          {/* ── CENTER: logo ── */}
          <Link to="/" className="header-logo" onClick={close}>
            <img src={logo} alt="Bikini World" className="logo-img" />
          </Link>

          {/* ── DESKTOP NAV (hidden on mobile) ── */}
          <nav className="desktop-nav">
            {navLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                className={({ isActive }) => `dnav-link ${isActive ? 'active' : ''}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* ── RIGHT: icons ── */}
          <div className="header-right">
            {/* Cart */}
            <Link to="/cart" className="hdr-icon-btn" title="Cart" onClick={close}>
              <FaShoppingCart />
              {cartCount > 0 && (
                <span className="cart-badge">{cartCount > 9 ? '9+' : cartCount}</span>
              )}
            </Link>

            {/* User */}
            <div className="user-menu-wrap">
              <button
                className="hdr-icon-btn user-menu-btn"
                onClick={() => setIsUserOpen(!isUserOpen)}
                aria-label="User menu"
              >
                {currentUser
                  ? <span className="avatar-circle">{currentUser.name?.charAt(0).toUpperCase() || 'U'}</span>
                  : <FaUser />}
                <FaChevronDown className={`chevron ${isUserOpen ? 'flipped' : ''}`} />
              </button>

              {isUserOpen && (
                <div className="user-dropdown">
                  {currentUser ? (
                    <>
                      <div className="ud-header">
                        <span className="ud-avatar">{currentUser.name?.charAt(0).toUpperCase() || 'U'}</span>
                        <div>
                          <strong>{currentUser.name || 'User'}</strong>
                          <small>{currentUser.email}</small>
                          {isAdmin && <span className="admin-pill">Admin</span>}
                        </div>
                      </div>
                      <div className="ud-body">
                        <Link to="/account" className="ud-item" onClick={close}><FaUser /> My Account</Link>
                        <Link to="/orders"  className="ud-item" onClick={close}><FaShoppingBag /> My Orders</Link>
                        {isAdmin && (
                          <Link to="/admin" className="ud-item ud-admin" onClick={close}><FaCog /> Admin Dashboard</Link>
                        )}
                        <hr className="ud-divider" />
                        <button className="ud-item ud-danger" onClick={handleLogout}><FaSignOutAlt /> Log Out</button>
                      </div>
                    </>
                  ) : (
                    <div className="ud-body">
                      <Link to="/login"    className="ud-item" onClick={close}><FaUser /> Sign In</Link>
                      <Link to="/register" className="ud-item ud-register" onClick={close}>Create Account</Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

      </header>

      {/* ── Mobile slide-out drawer ── */}
      <div className={`drawer-overlay ${isMenuOpen ? 'visible' : ''}`} onClick={() => setIsMenuOpen(false)} />
      <nav className={`mobile-drawer ${isMenuOpen ? 'open' : ''}`}>
        <div className="drawer-head">
          <img src={logo} alt="Bikini World" className="drawer-logo" />
          <button className="drawer-close" onClick={() => setIsMenuOpen(false)} aria-label="Close menu">
            <FaTimes />
          </button>
        </div>

        <ul className="drawer-nav">
          {navLinks.map((l) => (
            <li key={l.path}>
              <NavLink
                to={l.path}
                className={({ isActive }) => `drawer-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="drawer-link-icon">{l.icon}</span>
                {l.label}
                <FaChevronRight className="drawer-chevron" />
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="drawer-foot">
          <Link
            to="/shop?sort=discount"
            className="drawer-promo"
            onClick={() => setIsMenuOpen(false)}
          >
            <div>
              <strong>Summer Sale</strong>
              <span>Up to 50% off swimwear</span>
            </div>
            <FaPercent />
          </Link>

          {currentUser ? (
            <Link to="/account" className="drawer-cta" onClick={() => setIsMenuOpen(false)}>
              <FaUser /> My Account
            </Link>
          ) : (
            <Link to="/login" className="drawer-cta" onClick={() => setIsMenuOpen(false)}>
              <FaUser /> Sign In
            </Link>
          )}

          <div className="drawer-social">
            <a href="https://instagram.com" aria-label="Instagram"><FaInstagram /></a>
            <a href="https://facebook.com"  aria-label="Facebook"><FaFacebook /></a>
            <a href="https://tiktok.com"    aria-label="TikTok"><FaTiktok /></a>
          </div>
        </div>
      </nav>

      {isUserOpen && (
        <div className="overlay" onClick={() => setIsUserOpen(false)} />
      )}
    </>
  );
};

export default Header;
